import { InputRule, mergeAttributes } from "@tiptap/core";
import Mention from "@tiptap/extension-mention";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import { TextSelection } from "@tiptap/pm/state";
import type { SuggestionOptions } from "@tiptap/suggestion";

// An inline-atom node for reference chips, built on the Mention extension to
// reuse its suggestion machinery; carries an id, a label, and a chip type
// surfaced as `data-ref-type` for per-type CSS (color + icon).
//
// Both the node name and the JSON key holding the chip type are configurable,
// because they are load-bearing wire format: each chip family persists its own
// names in stored Tiptap JSON. The defaults (`aopReference` / `refType`) and the
// CSS class (`aop-ref`) predate this module's extraction from the AOP editor —
// stored agent operating procedures serialize those names and app CSS overrides
// target `.aop-ref`, so do not rename them. The mention family (task comments)
// overrides them to `mention` / `mentionType` for the same reason.

export const CHIP_REF_CLASS = "aop-ref";

/** Default node type name used in serialized Tiptap JSON. See warning above. */
export const CHIP_REF_NODE = "aopReference";

/** Default JSON attribute key holding a chip's type. See warning above. */
export const CHIP_TYPE_ATTR = "refType";

/** Attributes carried by every chip node. `refType` is the chip's type, read
 *  from whichever JSON key the family persists it under. */
export interface ChipRefAttrs {
  id: string;
  label: string;
  refType: string;
  /**
   * The property of the referenced entity this chip names, meaningful only
   * for `refType: "custom_model"` chips: one field key of the model `id`
   * points at. `null`/absent means the chip references the WHOLE model, which
   * is the only thing chips written before this attribute existed could mean.
   */
  field?: string | null;
}

/**
 * Markdown round-tripping for families whose stored value is a markdown string
 * rather than Tiptap JSON (the `{{tag}}` template surface). Plugs into
 * tiptap-markdown through the node's `markdown` storage: `serialize` writes a
 * chip back out as text, and `pattern`/`attrs` turn that text back into chips
 * when markdown is parsed. Omit it and the family stays JSON-only.
 */
export interface ChipMarkdownSpec {
  /** Chip → the markdown text it round-trips through (e.g. `{{path}}`). */
  serialize: (attrs: ChipRefAttrs) => string;
  /** Finds that text in parsed markdown. Matched globally; code is skipped. */
  pattern: RegExp;
  /** Match → chip attrs, or null to leave the matched text alone. */
  attrs: (match: RegExpExecArray) => ChipRefAttrs | null;
  /**
   * Extra rewrite of markdown-it's HTML before text-pattern chipify.
   * Mention chips live as markdown *links*, which parse to `<a>` rather than
   * remaining as the `[label](href)` source text.
   */
  updateDOM?: (root: HTMLElement) => void;
}

/** How a configured chip node names itself on the wire — the node type and the
 *  JSON attribute key its type lives under. */
export interface ChipNodeMeta {
  name: string;
  typeAttr: string;
}

export interface ChipExtensionOptions {
  /**
   * Node type name in the serialized Tiptap JSON (default {@link CHIP_REF_NODE}).
   * Persisted — pick per chip family and never change it after documents exist.
   */
  name?: string;
  /**
   * JSON attribute key holding the chip's type (default {@link CHIP_TYPE_ATTR}).
   * Persisted, same caveat as `name`. Always rendered as `data-ref-type`.
   */
  typeAttr?: string;
  /**
   * Fallback chip type for persisted nodes with no type attribute (documents
   * written before the family gained one). Returns "" for "no type".
   */
  inferType?: (attrs: { id: string; label: string }) => string;
  /** Prepended to the rendered chip text, e.g. `"@"` for mentions. */
  labelPrefix?: string;
  /** Suggestion config (from {@link createChipSuggestion}); omit to disable
   *  inline insertion (read-only rendering, paste-only chips). */
  suggestion?: Omit<SuggestionOptions, "editor">;
  /**
   * When it returns true for a chip's attrs, the chip renders as an anchor
   * whose href is the chip id (used for smart-link chips, whose id IS a URL).
   * Inside the editor a plain click still just places the cursor
   * (contenteditable swallows navigation).
   */
  renderAsAnchor?: (attrs: ChipRefAttrs) => boolean;
  /**
   * Text → chip: called with the whitespace-delimited token the caret just
   * left when the user types a space. Return the chip attrs to swap the token
   * for a chip (the space is kept), or null to leave it as plain text. Omit to
   * disable the conversion entirely (the `{{variable}}` surface does).
   */
  textToChip?: (token: string) => ChipRefAttrs | null;
  /**
   * Chip → text, the inverse of {@link ChipExtensionOptions.textToChip}: called
   * on Backspace when the caret sits immediately after a chip. Return the raw
   * text the chip should decompose back into, or null to fall through to the
   * default behaviour (delete the chip). Omit to always delete.
   */
  chipToText?: (attrs: ChipRefAttrs) => string | null;
  /**
   * Markdown round-tripping for this chip family; see {@link ChipMarkdownSpec}.
   * Required by surfaces whose value is a markdown string
   * (`RichChipEditor valueFormat="markdown"`), ignored by JSON surfaces.
   */
  markdown?: ChipMarkdownSpec;
  /**
   * Opt in to the `field` node attribute (see the doc on
   * {@link ChipRefAttrs.field}). Off by default: several chip families
   * (mention, variable, template) share this node's wire name
   * (`aopReference`/`mention`) across DIFFERENT stored documents, and
   * ProseMirror always serializes every declared attribute — turning this on
   * unconditionally would put `field: null` on every chip family's stored
   * JSON, not just the entity-chip-editor's custom-model refs that use it.
   */
  field?: boolean;
  /** Extra HTML attributes for a rendered chip, derived from its attrs (e.g. a
   *  per-entity colour tone). Never persisted. */
  renderAttrs?: (attrs: ChipRefAttrs) => Record<string, string>;
}

/**
 * The token the caret just left, as seen from the end of the text block: a run
 * of non-space characters preceded by the block start or whitespace, then the
 * space that triggered the rule.
 */
const TOKEN_BEFORE_SPACE = /(?:^|\s)(\S+) $/;

/** Read a chip node's attributes into a plain {@link ChipRefAttrs}. */
export function chipAttrsOf(attrs: Record<string, unknown>, typeAttr: string): ChipRefAttrs {
  const id = String(attrs.id ?? "");
  const field = attrs.field;
  return {
    id,
    label: String(attrs.label ?? id),
    refType: String(attrs[typeAttr] ?? ""),
    field: typeof field === "string" && field.length > 0 ? field : null,
  };
}

/**
 * Defines a node attribute that round-trips through a `data-ref-*` HTML
 * attribute. `nodeAttr` is the key on `node.attrs`; `suffix` names the HTML
 * attribute (`data-ref-<suffix>`) — they differ for the type attribute, whose
 * JSON key is per-family but whose HTML attribute is always `data-ref-type`.
 * `fallback` fills in a value absent from the persisted attrs.
 */
function dataRefAttr(
  nodeAttr: string,
  suffix: string,
  fallback?: (attrs: Record<string, unknown>) => string,
) {
  const htmlAttr = `data-ref-${suffix}`;
  return {
    default: null as string | null,
    parseHTML: (el: HTMLElement) => el.getAttribute(htmlAttr),
    renderHTML: (attrs: Record<string, unknown>) => {
      const value = attrs[nodeAttr] ?? fallback?.(attrs) ?? null;
      return value ? { [htmlAttr]: String(value) } : {};
    },
  };
}

// ============================================================================
// Markdown round-tripping
// ============================================================================

/** The only bit of tiptap-markdown's serializer state a chip atom needs. */
interface MarkdownWriter {
  write(content: string): void;
}

/** Markdown regions where chip text must stay literal: code, and text that
 *  already lives inside a chip. */
const CHIP_MARKDOWN_SKIP = "pre, code, [data-ref-id]";

/**
 * Rewrites every {@link ChipMarkdownSpec.pattern} match in `root`'s text nodes
 * into a chip span, which the node's own `parseHTML` then picks up. Runs on the
 * HTML tiptap-markdown produces, before ProseMirror parses it — so the markdown
 * pipeline stays responsible for blocks and marks, and this only re-chips text.
 */
function chipifyMarkdownText(root: HTMLElement, spec: ChipMarkdownSpec): void {
  const doc = root.ownerDocument;
  const walker = doc.createTreeWalker(root, 4 /* NodeFilter.SHOW_TEXT */);
  const texts: Text[] = [];
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const text = node as Text;
    if (!text.parentElement?.closest(CHIP_MARKDOWN_SKIP)) texts.push(text);
  }

  for (const text of texts) {
    // Fresh regex per node: a caller-supplied global pattern would carry
    // `lastIndex` across nodes.
    const pattern = new RegExp(spec.pattern.source, "g");
    const source = text.data;
    const fragment = doc.createDocumentFragment();
    let cursor = 0;
    for (let match = pattern.exec(source); match; match = pattern.exec(source)) {
      if (match[0] === "") break;
      const attrs = spec.attrs(match);
      if (!attrs) continue;
      if (match.index > cursor) fragment.append(source.slice(cursor, match.index));
      const span = doc.createElement("span");
      span.setAttribute("data-ref-id", attrs.id);
      span.setAttribute("data-ref-label", attrs.label);
      span.setAttribute("data-ref-type", attrs.refType);
      span.textContent = attrs.label;
      fragment.append(span);
      cursor = match.index + match[0].length;
    }
    if (cursor === 0) continue;
    if (cursor < source.length) fragment.append(source.slice(cursor));
    text.replaceWith(fragment);
  }
}

/** The wire names a configured chip extension uses. */
export function chipNodeMeta(options?: ChipExtensionOptions): ChipNodeMeta {
  return {
    name: options?.name ?? CHIP_REF_NODE,
    typeAttr: options?.typeAttr ?? CHIP_TYPE_ATTR,
  };
}

export function createChipExtension(options?: ChipExtensionOptions) {
  const renderAsAnchor = options?.renderAsAnchor;
  const textToChip = options?.textToChip;
  const chipToText = options?.chipToText;
  const markdown = options?.markdown;
  const labelPrefix = options?.labelPrefix ?? "";
  const { name: nodeName, typeAttr } = chipNodeMeta(options);
  const resolveType = (attrs: Record<string, unknown>): string =>
    String(attrs[typeAttr] ?? options?.inferType?.(chipAttrsOf(attrs, typeAttr)) ?? "");
  return Mention.extend({
    name: nodeName,

    addAttributes() {
      return {
        id: dataRefAttr("id", "id"),
        label: dataRefAttr("label", "label"),
        [typeAttr]: dataRefAttr(typeAttr, "type", resolveType),
        // Opt-in (see `field` on ChipExtensionOptions) — only declared for
        // families that ask for it, so every other family's stored JSON is
        // untouched.
        ...(options?.field ? { field: dataRefAttr("field", "field") } : {}),
      };
    },

    parseHTML() {
      return [{ tag: `span[data-ref-id]` }, { tag: `a[data-ref-id]` }];
    },

    renderHTML({ node, HTMLAttributes }) {
      const id = String(node.attrs.id ?? "");
      const label = labelPrefix + String(node.attrs.label ?? node.attrs.id ?? "");
      const refType = resolveType(node.attrs);
      const extraAttrs = options?.renderAttrs?.({ id, label, refType, field: null }) ?? {};
      if (renderAsAnchor?.({ id, label, refType })) {
        return [
          "a",
          mergeAttributes(
            {
              class: CHIP_REF_CLASS,
              href: id,
              title: id,
              target: "_blank",
              rel: "noopener noreferrer nofollow",
            },
            HTMLAttributes,
            extraAttrs,
          ),
          label,
        ];
      }
      return [
        "span",
        mergeAttributes({ class: CHIP_REF_CLASS }, HTMLAttributes, extraAttrs),
        label,
      ];
    },

    renderText({ node }) {
      return labelPrefix + String(node.attrs.label ?? node.attrs.id ?? "");
    },

    // tiptap-markdown reads `storage.markdown` off each extension: `serialize`
    // writes the chip back out as text, `parse.updateDOM` re-chips that text.
    addStorage() {
      const parent = (this.parent?.() ?? {}) as Record<string, unknown>;
      if (!markdown) return parent;
      return {
        ...parent,
        markdown: {
          serialize(state: MarkdownWriter, node: ProseMirrorNode) {
            state.write(markdown.serialize(chipAttrsOf(node.attrs, typeAttr)));
          },
          parse: {
            updateDOM(element: HTMLElement) {
              markdown.updateDOM?.(element);
              chipifyMarkdownText(element, markdown);
            },
          },
        },
      };
    },

    addInputRules() {
      const parent = this.parent?.() ?? [];
      if (!textToChip) return parent;
      return [
        ...parent,
        new InputRule({
          find: TOKEN_BEFORE_SPACE,
          handler: ({ state, range, match }) => {
            const token = match[1];
            if (!token) return;
            const attrs = textToChip(token);
            if (!attrs) return;
            // `range.from` can't be trusted: chip atoms contribute their whole
            // label to the matched string (see renderText) while occupying a
            // single document position, so the match length and the position
            // delta disagree. Anchor on the caret instead and verify the token
            // really is text in the document before replacing it.
            //
            // The trailing space is already in the document when the rule runs
            // from simulated input (`insertContent(…, { applyInputRules: true })`)
            // but not when the user actually types it — hence the two cases.
            const typed = state.doc.textBetween(Math.max(range.to - 1, 0), range.to) !== " ";
            const end = typed ? range.to : range.to - 1;
            const start = end - token.length;
            if (start < 0 || state.doc.textBetween(start, end) !== token) return;
            const chip = this.type.create(attrs);
            state.tr.replaceWith(start, end, typed ? [chip, state.schema.text(" ")] : chip);
          },
        }),
      ];
    },

    addKeyboardShortcuts() {
      const parent = this.parent?.() ?? {};
      if (!chipToText) return parent;
      return {
        ...parent,
        // Backspace right after a chip that maps back to text un-converts it
        // rather than deleting it, so the raw text survives an undo-by-typing.
        // Everything else falls through to Mention's own Backspace handling.
        Backspace: (props) => {
          // Runs through `commands.command` (like Mention's own Backspace) so
          // the edit lands on the caller's transaction — Tiptap replays
          // captured transactions, and dispatching directly would double-apply.
          const unconverted = this.editor.commands.command(({ tr, state, dispatch }) => {
            const { $from, empty } = state.selection;
            const before = $from.nodeBefore;
            if (!empty || !before || before.type !== this.type) return false;
            const text = chipToText(chipAttrsOf(before.attrs, typeAttr));
            if (!text) return false;
            if (dispatch) {
              const from = $from.pos - before.nodeSize;
              tr.replaceWith(from, $from.pos, state.schema.text(text));
              tr.setSelection(TextSelection.create(tr.doc, from + text.length));
              tr.scrollIntoView();
            }
            return true;
          });
          return unconverted || (parent.Backspace?.(props) ?? false);
        },
      };
    },
  }).configure({
    HTMLAttributes: { class: CHIP_REF_CLASS },
    ...(options?.suggestion ? { suggestion: options.suggestion } : {}),
  });
}
